
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface SecurityEvent {
  id: string;
  event_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  user_id?: string;
  ip_address?: string;
  user_agent?: string;
  event_data: Record<string, any>;
  risk_score: number;
  resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  created_at: string;
}

export const useSecurityEvents = () => {
  const [loading, setLoading] = useState(false);
  const [securityEvents, setSecurityEvents] = useState<SecurityEvent[]>([]);
  const { toast } = useToast();

  const fetchSecurityEvents = async (filters?: {
    severity?: string;
    eventType?: string;
    dateFrom?: string;
    dateTo?: string;
  }) => {
    setLoading(true);
    try {
      let query = supabase
        .from('security_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }
      if (filters?.eventType) {
        query = query.eq('event_type', filters.eventType);
      }
      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }
      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;
      setSecurityEvents((data || []) as SecurityEvent[]);
    } catch (error: any) {
      console.error('Error fetching security events:', error);
      toast({
        title: "Error",
        description: "Failed to load security events",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resolveSecurityEvent = async (eventId: string, resolutionNotes?: string) => {
    try {
      const { error } = await supabase
        .from('security_events')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString()
        })
        .eq('id', eventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Security event resolved successfully",
      });

      fetchSecurityEvents();
    } catch (error: any) {
      console.error('Error resolving security event:', error);
      toast({
        title: "Error",
        description: "Failed to resolve security event",
        variant: "destructive",
      });
    }
  };

  return {
    loading,
    securityEvents,
    fetchSecurityEvents,
    resolveSecurityEvent,
  };
};
